import React from 'react';

/**
 * The panel's one table.
 *
 * Every page previously hand-rolled its own `<table>` with slightly different
 * padding, sort affordances and empty states, which is how the Catalog table
 * ended up twelve columns wide inside a container that couldn't show them.
 * The rules baked in here:
 *
 *  - Numbers are right-aligned and `tabular-nums`, so columns of figures
 *    compare vertically at a glance. Text is left-aligned. This is the
 *    single biggest readability win on a dense ops table and the easiest to
 *    forget per-page.
 *  - A sortable header states its direction with an arrow, and only the
 *    active column shows one — an arrow on every header tells you nothing.
 *  - Loading renders skeleton rows at the real column count, so the layout
 *    doesn't jump when data lands.
 *  - `null` renders as "-", never as 0. Unknown and zero are different facts.
 */
function SortArrow({ active, order }) {
  if (!active) return null;
  return (
    <span aria-hidden="true" className="ml-1 text-ink-muted">
      {order === 'desc' ? '↓' : '↑'}
    </span>
  );
}

export default function DataTable({
  columns,
  rows,
  rowKey,
  sort,
  onSort,
  onRowClick,
  loading = false,
  empty = null,
  maxHeight,
  dense = false,
}) {
  const cell = dense ? 'px-3 py-1.5' : 'px-3 py-2.5';

  if (!loading && (!rows || rows.length === 0)) return empty;

  const alignFor = (c) => (c.align === 'right' ? 'text-right tabular-nums' : c.align === 'center' ? 'text-center' : 'text-left');

  return (
    <div
      className="overflow-auto rounded-xl border border-hairline bg-surface shadow-sm"
      style={maxHeight ? { maxHeight } : undefined}
    >
      <table className="w-full border-collapse text-left text-xs">
        <thead className="sticky top-0 z-10 bg-surface">
          <tr className="border-b border-hairline">
            {columns.map((c) => {
              const active = sort?.key === c.key;
              return (
                <th
                  key={c.key}
                  scope="col"
                  style={c.width ? { width: c.width } : undefined}
                  className={`${cell} font-medium text-ink-muted ${alignFor(c)} ${
                    c.sortable && onSort ? 'cursor-pointer select-none hover:text-ink' : ''
                  }`}
                  onClick={c.sortable && onSort ? () => onSort(c.key) : undefined}
                  aria-sort={active ? (sort.order === 'desc' ? 'descending' : 'ascending') : undefined}
                >
                  {c.label}
                  <SortArrow active={active} order={sort?.order} />
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-hairline">
          {loading
            ? Array.from({ length: 6 }).map((_, i) => (
                <tr key={`skeleton-${i}`}>
                  {columns.map((c) => (
                    <td key={c.key} className={cell}>
                      <span className="inline-block h-3 w-full max-w-[90px] animate-pulse rounded bg-hairline" />
                    </td>
                  ))}
                </tr>
              ))
            : rows.map((row, i) => (
                <tr
                  key={rowKey ? rowKey(row, i) : i}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={onRowClick ? 'cursor-pointer hover:bg-surface-page' : undefined}
                >
                  {columns.map((c) => {
                    const raw = c.render ? c.render(row) : row[c.key];
                    const shown = raw == null || raw === '' ? <span className="text-ink-muted">-</span> : raw;
                    return (
                      <td key={c.key} className={`${cell} ${alignFor(c)} text-ink-secondary`}>
                        {shown}
                      </td>
                    );
                  })}
                </tr>
              ))}
        </tbody>
      </table>
    </div>
  );
}
